// src/seo/useSEO.js
import { useEffect } from "react";

/**
 * Hook SEO minimaliste compatible React 19 (sans dépendance externe).
 * - Met à jour <title>, meta description, canonical.
 * - Open Graph + Twitter Card avec image absolue.
 * - Option: JSON-LD via 'jsonLd' (objet ou fonction qui retourne un objet).
 */

const SITE_NAME = "Maxime H — Photographe culinaire";
const SITE_URL  = typeof window !== "undefined" ? window.location.origin : "https://www.maximeh.fr";
const DEFAULT_IMAGE = "/images/og-default.jpg"; // place ce fichier dans /public/images (1200x630 recommandé)
const TWITTER_HANDLE = "@maximehphoto"; // mets ton handle X/Twitter si tu en as un (sinon laisse vide)

function ensureAbsolute(url) {
  if (!url) return `${SITE_URL}${DEFAULT_IMAGE}`;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // Traite /chemin comme absolu basé sur le domaine
  if (url.startsWith("/")) return `${SITE_URL}${url}`;
  // Sinon: chemin relatif → le résout proprement
  try {
    return new URL(url, SITE_URL).toString();
  } catch {
    return `${SITE_URL}${DEFAULT_IMAGE}`;
  }
}

function setMeta(nameOrProp, isProperty, content) {
  if (!content) return;
  const selector = isProperty
    ? `meta[property="${nameOrProp}"]`
    : `meta[name="${nameOrProp}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(isProperty ? "property" : "name", nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(json) {
  if (!json) return;
  let el = document.querySelector('script[type="application/ld+json"][data-seo="true"]');
  const payload = typeof json === "function" ? json() : json;
  const jsonText = JSON.stringify(payload);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", "true");
    el.text = jsonText;
    document.head.appendChild(el);
  } else {
    el.text = jsonText;
  }
}

export default function useSEO({
  title = SITE_NAME,
  description = "Photographe culinaire — produits, portraits, événements et lieux. Basé en France, Belgique, Luxembourg. Images prêtes pour web, réseaux et print.",
  canonical,                    // ex: `${window.location.origin}/galerie`
  image = DEFAULT_IMAGE,        // ex: "/images/og-default.jpg"
  locale = "fr_FR",
  jsonLd = null,                // optionnel: objet JSON-LD ou fonction retournant l'objet
} = {}) {
  useEffect(() => {
    // Title
    if (title) document.title = title;

    // Canonical (si non fourni, on prend l’URL actuelle)
    const canonicalUrl = canonical || (typeof window !== "undefined" ? window.location.href : `${SITE_URL}/`);
    setLink("canonical", canonicalUrl);

    // Description
    setMeta("description", false, description);

    // Open Graph
    const absoluteImage = ensureAbsolute(image);
    setMeta("og:type", true, "website");
    setMeta("og:site_name", true, SITE_NAME);
    setMeta("og:title", true, title);
    setMeta("og:description", true, description);
    setMeta("og:url", true, canonicalUrl);
    setMeta("og:image", true, absoluteImage);
    setMeta("og:locale", true, locale);

    // Twitter
    setMeta("twitter:card", false, "summary_large_image");
    setMeta("twitter:title", false, title);
    setMeta("twitter:description", false, description);
    setMeta("twitter:image", false, absoluteImage);
    if (TWITTER_HANDLE) setMeta("twitter:site", false, TWITTER_HANDLE);

    // JSON-LD (optionnel)
    setJsonLd(jsonLd);

    // Pas de cleanup nécessaire: on garde les balises pour l'historique de navigation
  }, [title, description, canonical, image, locale, jsonLd]);
}